'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useResumeBuilderStore } from '@/stores/resume-builder-store';
import { Button } from '@/components/ui/forms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Check, ChevronDown, LayoutGrid, X } from 'lucide-react';
import { RESUME_TEMPLATES } from '@/lib/resume/templates/registry';
import {
  RESUME_COLOR_SCHEMES,
  getPrioritizedColorSchemeIds,
  isColorSchemeAvailableForTemplate,
  resolveTemplateStyleFamily,
} from '@/lib/resume/templates/color-schemes';

/** Debounce for the hover preview, so a fast mouse pass across the list
 *  doesn't re-render the (potentially expensive) resume HTML for every
 *  row it crosses — only the row the pointer actually settles on. */
const HOVER_PREVIEW_DEBOUNCE_MS = 80;

export default function StyleSelector() {
  const { resume, updateSettings, setPreviewStyle, clearPreviewStyle } = useResumeBuilderStore();
  const [open, setOpen] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingHover = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const schedulePreview = useCallback(
    (templateId: string, colorSchemeId: string | undefined) => {
      cancelPendingHover();
      hoverTimeoutRef.current = setTimeout(() => {
        hoverTimeoutRef.current = null;
        setPreviewStyle(templateId, colorSchemeId ?? null);
      }, HOVER_PREVIEW_DEBOUNCE_MS);
    },
    [cancelPendingHover, setPreviewStyle]
  );

  // Always leave with the preview cleared: unmounting mid-hover, the
  // dropdown closing, or the mouse leaving the list should never leave a
  // stale hover preview showing instead of the saved style.
  useEffect(() => {
    return () => {
      cancelPendingHover();
      clearPreviewStyle();
    };
  }, [cancelPendingHover, clearPreviewStyle]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) {
        cancelPendingHover();
        clearPreviewStyle();
      }
    },
    [cancelPendingHover, clearPreviewStyle]
  );

  const handleDropdownMouseLeave = useCallback(() => {
    cancelPendingHover();
    clearPreviewStyle();
  }, [cancelPendingHover, clearPreviewStyle]);

  if (!resume) return null;

  const prioritizedTemplateIds = ['professional', 'focus-sidebar', 'ats-optimized'];
  const templateOptions = [
    ...prioritizedTemplateIds.flatMap((templateId) =>
      RESUME_TEMPLATES.filter((template) => template.id === templateId)
    ),
    ...RESUME_TEMPLATES.filter((template) => !prioritizedTemplateIds.includes(template.id)),
  ];

  const availableColorSchemes = RESUME_COLOR_SCHEMES.filter((scheme) =>
    isColorSchemeAvailableForTemplate(scheme, resume.settings.templateId)
  );
  const prioritizedColorSchemeIds = getPrioritizedColorSchemeIds(resume.settings.templateId);

  const getAvailableSchemesForTemplate = (templateId: string) =>
    RESUME_COLOR_SCHEMES.filter((scheme) => isColorSchemeAvailableForTemplate(scheme, templateId));

  const getPreferredColorSchemeId = (templateId: string) => {
    const schemes = getAvailableSchemesForTemplate(templateId);
    if (schemes.length === 0) return undefined;
    const preferredOrder = getPrioritizedColorSchemeIds(templateId);
    return preferredOrder.find((schemeId) => schemes.some((scheme) => scheme.id === schemeId)) ?? schemes[0]?.id;
  };

  const colorOptions = [
    ...prioritizedColorSchemeIds.flatMap((schemeId) =>
      availableColorSchemes.filter((scheme) => scheme.id === schemeId)
    ),
    ...availableColorSchemes.filter((scheme) => !prioritizedColorSchemeIds.includes(scheme.id)),
  ];

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2">
          <LayoutGrid className="h-4 w-4" />
          <span>Style</span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[560px] max-w-[92vw] p-0"
        onMouseLeave={handleDropdownMouseLeave}
      >
        <div className="flex items-center justify-between border-b border-border px-2 py-1.5">
          <span className="text-overline text-muted-foreground">Style</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-caption"
            onClick={() => handleOpenChange(false)}
          >
            <X className="mr-1 h-3.5 w-3.5" />
            Close
          </Button>
        </div>
        <div className="grid grid-cols-2 divide-x divide-border">
          <div className="p-2">
            <div className="px-2 pb-2 pt-1 text-overline text-muted-foreground">
              Template
            </div>
            <div className="max-h-64 overflow-y-auto pr-1">
              {templateOptions.map((template) => {
                const isSelected = template.id === resume.settings.templateId;

                // Same "does the current color scheme still apply" logic the
                // click handler uses, so the hover preview shows exactly the
                // color scheme that selecting this template would land on.
                const nextSchemes = getAvailableSchemesForTemplate(template.id);
                const hasCurrentScheme = nextSchemes.some(
                  (scheme) => scheme.id === resume.settings.colorSchemeId
                );
                const previewColorSchemeId = hasCurrentScheme
                  ? resume.settings.colorSchemeId
                  : getPreferredColorSchemeId(template.id);

                return (
                  <DropdownMenuItem
                    key={template.id}
                    onSelect={(event) => {
                      event.preventDefault();
                      const nextTemplateId = template.id;
                      const templateFamilyChanged =
                        resolveTemplateStyleFamily(resume.settings.templateId) !==
                        resolveTemplateStyleFamily(nextTemplateId);
                      const shouldUpdateColorScheme = templateFamilyChanged || !hasCurrentScheme;

                      cancelPendingHover();
                      clearPreviewStyle();
                      updateSettings({
                        templateId: nextTemplateId,
                        ...(shouldUpdateColorScheme && previewColorSchemeId ? { colorSchemeId: previewColorSchemeId } : {}),
                      });
                    }}
                    onMouseEnter={() => schedulePreview(template.id, previewColorSchemeId)}
                    selected={isSelected}
                    className="flex-col items-start gap-0.5 py-1"
                  >
                    <span className="flex w-full items-center justify-between text-label">
                      {template.name}
                      {isSelected ? <Check className="h-3.5 w-3.5" /> : null}
                    </span>
                    <span className="w-full truncate text-caption text-muted-foreground">
                      {template.description}
                    </span>
                  </DropdownMenuItem>
                );
              })}
            </div>
          </div>

          <div className="p-2">
            <div className="px-2 pb-2 pt-1 text-overline text-muted-foreground">
              Color
            </div>
            <div className="max-h-64 overflow-y-auto pl-1 pr-1">
              {colorOptions.map((scheme) => {
                const isSelected = scheme.id === resume.settings.colorSchemeId;

                return (
                  <DropdownMenuItem
                    key={scheme.id}
                    onSelect={(event) => {
                      event.preventDefault();
                      cancelPendingHover();
                      clearPreviewStyle();
                      updateSettings({ colorSchemeId: scheme.id });
                    }}
                    onMouseEnter={() => schedulePreview(resume.settings.templateId, scheme.id)}
                    selected={isSelected}
                    className="py-1"
                  >
                    <div
                      className="h-3.5 w-3.5 rounded-full border"
                      style={{
                        background: `linear-gradient(135deg, ${scheme.colors.secondary} 0%, ${scheme.colors.primary} 60%, ${scheme.colors.accent} 100%)`,
                      }}
                    />
                    <span className="flex-1">{scheme.name}</span>
                    {isSelected ? <Check className="h-3.5 w-3.5" /> : null}
                  </DropdownMenuItem>
                );
              })}
            </div>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
